import type { HlsTimelineViewport } from '@repo/hls-flussonic-video-player';

import type { VideoBroadcastPlaybackMode } from '../types';

const PLAYER_SEEK_CONFIRM_PROGRESS_MS = 250;
const PLAYER_PROGRESS_BACKWARD_TOLERANCE_MS = 0;
const EXTERNAL_SEEK_TIMELINE_VIEWPORT_WINDOW_MS = 5 * 60_000;

export function hasConfirmedPendingSeekPlayback({
  reportedTimeMs,
  targetTimeMs,
}: {
  reportedTimeMs: number;
  targetTimeMs: number;
}) {
  return reportedTimeMs >= targetTimeMs + PLAYER_SEEK_CONFIRM_PROGRESS_MS;
}

export function isPlayerProgressTimeAccepted({
  currentTimeMs,
  reportedTimeMs,
}: {
  currentTimeMs: number | null;
  reportedTimeMs: number;
}) {
  if (currentTimeMs == null) {
    return true;
  }

  return reportedTimeMs >= currentTimeMs - PLAYER_PROGRESS_BACKWARD_TOLERANCE_MS;
}

export function getCenteredTimelineViewportAfterExternalSeek({
  liveEdgeMs,
  playbackMode,
  timeMs,
  viewport,
}: {
  liveEdgeMs: number | null;
  playbackMode: VideoBroadcastPlaybackMode;
  timeMs: number;
  viewport: HlsTimelineViewport | null;
}): HlsTimelineViewport | null {
  if (!Number.isFinite(timeMs)) {
    return null;
  }

  if (viewport?.mode === 'fixed') {
    if (isTimeInsideTimelineViewport(timeMs, viewport)) {
      return null;
    }

    return getCenteredTimelineViewport({
      timeMs,
      windowMs: viewport.endMs - viewport.startMs,
    });
  }

  if (viewport?.mode === 'live-follow') {
    if (viewport.windowMs == null) {
      return null;
    }

    if (
      liveEdgeMs != null &&
      isTimeInsideTimelineRange({
        endMs: liveEdgeMs,
        startMs: liveEdgeMs - viewport.windowMs,
        timeMs,
      })
    ) {
      return null;
    }

    return getCenteredTimelineViewport({
      timeMs,
      windowMs: viewport.windowMs,
    });
  }

  if (playbackMode !== 'live') {
    return null;
  }

  const liveWindowEndMs = liveEdgeMs ?? Date.now();
  if (
    isTimeInsideTimelineRange({
      endMs: liveWindowEndMs,
      startMs: liveWindowEndMs - EXTERNAL_SEEK_TIMELINE_VIEWPORT_WINDOW_MS,
      timeMs,
    })
  ) {
    return null;
  }

  return getCenteredTimelineViewport({
    timeMs,
    windowMs: EXTERNAL_SEEK_TIMELINE_VIEWPORT_WINDOW_MS,
  });
}

function isTimeInsideTimelineViewport(timeMs: number, viewport: Extract<HlsTimelineViewport, { mode: 'fixed' }>) {
  return isTimeInsideTimelineRange({
    endMs: viewport.endMs,
    startMs: viewport.startMs,
    timeMs,
  });
}

function isTimeInsideTimelineRange({ endMs, startMs, timeMs }: { endMs: number; startMs: number; timeMs: number }) {
  return timeMs >= startMs && timeMs <= endMs;
}

function getCenteredTimelineViewport({
  timeMs,
  windowMs,
}: {
  timeMs: number;
  windowMs: number;
}): HlsTimelineViewport | null {
  const safeWindowMs = Number.isFinite(windowMs) && windowMs > 0 ? windowMs : EXTERNAL_SEEK_TIMELINE_VIEWPORT_WINDOW_MS;

  const halfWindowMs = safeWindowMs / 2;

  return {
    mode: 'fixed',
    endMs: Math.round(timeMs + halfWindowMs),
    startMs: Math.round(timeMs - halfWindowMs),
  };
}
